import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSearch } from "../context/SearchContext";
import { useOnboarding } from "../context/OnboardingContext";
import { Search, BookOpen, Star, LogOut, Sparkles, Wallet, Filter, HelpCircle, RefreshCw, Settings as SettingsIcon, Briefcase } from "lucide-react";
import { Button } from "./ui/button";
import NotificationBell from "./NotificationBell";
import ThemeToggle from "./ThemeToggle";
import UpTickMark from "./UpTickMark";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { openSearch } = useSearch();
  const { start: startTour } = useOnboarding();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <>
      <header
        className="sticky top-0 z-50 bg-[#F9F8F6]/85 backdrop-blur-md border-b border-[#EAE6E1]"
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <UpTickMark size={32} className="transition-soft group-hover:scale-105" />
            <span className="font-heading text-xl font-bold tracking-tight text-[#2A2F2B] dark:text-[#E6EDF3]">
              UpTick
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {user && (
              <Link
                to="/dashboard"
                data-testid="nav-dashboard"
                className={`px-4 py-2 text-sm font-medium rounded-full transition-soft ${
                  location.pathname === "/dashboard"
                    ? "bg-[#EAE6E1] text-[#2A2F2B]"
                    : "text-[#6B7068] hover:text-[#2A2F2B] hover:bg-[#EAE6E1]/50"
                }`}
              >
                Dashboard
              </Link>
            )}
            {user && (
              <Link
                to="/watchlist"
                data-testid="nav-watchlist"
                className={`px-4 py-2 text-sm font-medium rounded-full transition-soft ${
                  location.pathname === "/watchlist"
                    ? "bg-[#EAE6E1] text-[#2A2F2B]"
                    : "text-[#6B7068] hover:text-[#2A2F2B] hover:bg-[#EAE6E1]/50"
                }`}
              >
                <Star className="w-4 h-4 inline mr-1.5" strokeWidth={1.5} />
                Watchlist
              </Link>
            )}
            {user && (
              <Link
                to="/paper"
                data-testid="nav-paper"
                className={`px-4 py-2 text-sm font-medium rounded-full transition-soft ${
                  location.pathname === "/paper"
                    ? "bg-[#EAE6E1] text-[#2A2F2B]"
                    : "text-[#6B7068] hover:text-[#2A2F2B] hover:bg-[#EAE6E1]/50"
                }`}
              >
                <Wallet className="w-4 h-4 inline mr-1.5" strokeWidth={1.5} />
                Paper
              </Link>
            )}
            <Link
              to="/screener"
              data-testid="nav-screener"
              className={`px-4 py-2 text-sm font-medium rounded-full transition-soft ${
                location.pathname === "/screener"
                  ? "bg-[#EAE6E1] text-[#2A2F2B]"
                  : "text-[#6B7068] hover:text-[#2A2F2B] hover:bg-[#EAE6E1]/50"
              }`}
            >
              <Filter className="w-4 h-4 inline mr-1.5" strokeWidth={1.5} />
              Screener
            </Link>
            {user && (
              <Link
                to="/wheel"
                data-testid="nav-wheel"
                className={`px-4 py-2 text-sm font-medium rounded-full transition-soft ${
                  location.pathname.startsWith("/wheel")
                    ? "bg-[#EAE6E1] text-[#2A2F2B]"
                    : "text-[#6B7068] hover:text-[#2A2F2B] hover:bg-[#EAE6E1]/50"
                }`}
              >
                <RefreshCw className="w-4 h-4 inline mr-1.5" strokeWidth={1.5} />
                Wheel
              </Link>
            )}
            {user && (
              <Link
                to="/broker"
                data-testid="nav-broker"
                className={`px-4 py-2 text-sm font-medium rounded-full transition-soft ${
                  location.pathname.startsWith("/broker")
                    ? "bg-[#EAE6E1] text-[#2A2F2B]"
                    : "text-[#6B7068] hover:text-[#2A2F2B] hover:bg-[#EAE6E1]/50"
                }`}
              >
                <Briefcase className="w-4 h-4 inline mr-1.5" strokeWidth={1.5} />
                Broker
              </Link>
            )}
            <Link
              to="/learn"
              data-testid="nav-learn"
              className={`px-4 py-2 text-sm font-medium rounded-full transition-soft ${
                location.pathname === "/learn"
                  ? "bg-[#EAE6E1] text-[#2A2F2B]"
                  : "text-[#6B7068] hover:text-[#2A2F2B] hover:bg-[#EAE6E1]/50"
              }`}
            >
              <BookOpen className="w-4 h-4 inline mr-1.5" strokeWidth={1.5} />
              Learn
            </Link>
          </nav>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button
              onClick={openSearch}
              data-testid="open-search-btn"
              className="flex items-center gap-2 px-3 py-2 rounded-full border border-[#EAE6E1] bg-white hover:border-[#547A8F] transition-soft text-sm text-[#6B7068]"
            >
              <Search className="w-4 h-4" strokeWidth={1.5} />
              <span className="hidden sm:inline">Search assets</span>
              <kbd className="hidden md:inline-flex font-mono-ticker text-[10px] px-1.5 py-0.5 bg-[#F9F8F6] border border-[#EAE6E1] rounded">
                ⌘K
              </kbd>
            </button>

            {user ? (
              <>
                <button
                  onClick={startTour}
                  data-testid="open-tour-btn"
                  className="hidden md:inline-flex p-2 rounded-full hover:bg-[#EAE6E1]/60 transition-soft text-[#2A2F2B]"
                  title="Take a tour"
                  aria-label="Take a tour"
                >
                  <HelpCircle className="w-5 h-5" strokeWidth={1.5} />
                </button>
                <NotificationBell />
                <Link
                  to="/settings"
                  data-testid="nav-settings"
                  className="hidden sm:inline-flex p-2 rounded-full hover:bg-[#EAE6E1]/60 transition-soft text-[#2A2F2B]"
                  title="Settings"
                  aria-label="Settings"
                >
                  <SettingsIcon className="w-5 h-5" strokeWidth={1.5} />
                </Link>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  className="rounded-full text-sm text-[#6B7068] hover:text-[#2A2F2B]"
                >
                  <LogOut className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                  <span className="hidden sm:inline">Sign out</span>
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => navigate("/login")}
                  data-testid="nav-login-btn"
                  className="rounded-full text-sm"
                >
                  Sign in
                </Button>
                <Button
                  onClick={() => navigate("/register")}
                  data-testid="nav-register-btn"
                  className="rounded-full text-sm bg-[#2A2F2B] hover:bg-[#547A8F] text-white"
                >
                  Get started
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
}
